import Helpers from 'Modules/Helpers';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';

interface GreetingFile {
  path: string;
  text?: string;
  type: string;
  yearTaken?: number | null;
}

interface OrderInfo {
  deceasedNickname: string;
  memorialFund: unknown;
  obituaryId: string;
  orderId: string;
  possessiveName: string;
}

export function useCreateComment() {
  const store = useStore();
  const publishing = ref(false);

  // Computed from store state
  const files = computed(() => store.state.comments.files);
  const name = computed(() => store.state.createComment.name);
  const comment = computed(() => store.state.createComment.comment);
  const email = computed(() => store.state.createComment.email);

  const hasFiles = computed(() => !!(files.value && files.value.length));

  // Actions
  function resetFiles() {
    return store.dispatch('comments/resetFiles');
  }

  function resetInput() {
    return store.dispatch('createComment/reset');
  }

  function resetDonation() {
    return store.dispatch('donations/resetDonation');
  }

  function persist(data: Record<string, unknown>) {
    return store.dispatch('comments/createObituaryComment', data);
  }

  function toggleModal() {
    return store.dispatch('createComment/toggleModal');
  }

  /** Publishes the greeting with its uploaded files, then closes the modal. */
  function publish(orderInfo: OrderInfo) {
    if (publishing.value || !(name.value && comment.value)) {
      return;
    }

    publishing.value = true;

    const data = {
      comment: comment.value,
      // Page-level details forwarded so the comments store can hand them
      // to the share modal it opens after a successful create.
      deceasedName: orderInfo.deceasedNickname,
      email: email.value,
      files: (files.value as GreetingFile[]).map(
        ({ type, path, yearTaken, text }) => ({
          path,
          text: text || '',
          type,
          yearTaken: yearTaken || null,
        })
      ),
      id: 'new',
      loading: true,
      memorialFund: orderInfo.memorialFund,
      name: name.value,
      obituaryId: orderInfo.obituaryId,
      orderId: orderInfo.orderId,
      possessiveName: orderInfo.possessiveName,
    };
    const withFiles = hasFiles.value;

    persist(data)
      .then(() => {
        if (withFiles) {
          Helpers.gtmObituaryEvent('image');
        }
        Helpers.gtmObituaryEvent('greeting');
        Helpers.obituaryGreetingsLog(
          'PublishedGreeting',
          orderInfo.obituaryId
        );
        publishing.value = false;
        resetInput();
        resetFiles();
        resetDonation();
        toggleModal();
      })
      .catch((error) => {
        publishing.value = false;
        // biome-ignore lint/suspicious/noAlert: no in-page error UI exists for this form yet
        alert(
          error.serverMessage || lavendlaObituary.createComment.publishError
        );
      });
  }

  return {
    // State
    files,
    name,
    comment,
    email,
    publishing,
    // Computed
    hasFiles,
    // Actions
    publish,
    resetFiles,
    resetInput,
    resetDonation,
    persist,
    toggleModal,
  };
}
